<script setup lang="ts">
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphDialog } from '@/components/base/dialog';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    mode: 'create' | 'add'
}>();

const emit = defineEmits<{
    submit: [payload: MemberSearchSubmit]
}>();

export interface MemberSearchResult {
    id: number
    displayName: string
    dateOfBirth: string | null
    institutionName: string
    email: string | null
    groupId: number | null
}

export interface MemberSearchSubmit {
    memberIds: number[]
    note: string
    done: (_success: boolean) => void
}

const open = defineModel('state', { type: Boolean, default: false });

const { i18n } = useI18n();
const { notify } = useNotifications();

const query = ref('');
const note = ref('');
const results = ref<MemberSearchResult[]>([]);
const selectedIds = ref<number[]>([]);
const searching = ref(false);

let debounceTimer: ReturnType<typeof setTimeout> | undefined;

function search() {
    if (query.value.trim().length < 2) {
        results.value = [];
        return;
    }
    searching.value = true;
    axios.get(`/federations/${props.federationId}/record-links/member-search`, { params: { query: query.value.trim() } })
        .then((response) => {
            results.value = response.data.data;
        })
        .catch(() => notify.error(i18n.t('error.loadingData')))
        .finally(() => {
            searching.value = false;
        });
}

watch(query, () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(search, 300);
});

watch(open, (isOpen) => {
    if (!isOpen) {
        query.value = '';
        note.value = '';
        results.value = [];
        selectedIds.value = [];
    }
});

const isSelected = (id: number) => selectedIds.value.includes(id);

function toggle(id: number) {
    selectedIds.value = isSelected(id)
        ? selectedIds.value.filter((selectedId) => selectedId !== id)
        : [...selectedIds.value, id];
}

const minSelection = computed(() => props.mode === 'create' ? 2 : 1);
const submitDisabled = computed(() => selectedIds.value.length < minSelection.value);

const title = computed(() => props.mode === 'create'
    ? i18n.t('Datensätze verknüpfen')
    : i18n.t('Datensatz hinzufügen'));

function onSubmit(event: { done: (_success: boolean) => void }) {
    emit('submit', { memberIds: [...selectedIds.value], note: note.value.trim(), done: event.done });
}
</script>

<template>
    <SphDialog
        v-model:state="open"
        variant="form"
        :title="title"
        :submit-button-label="mode === 'create' ? $t('Verknüpfen') : $t('Hinzufügen')"
        :disable-submit="submitDisabled"
        @submit="onSubmit"
    >
        <p v-if="mode === 'create'">
            {{ $t('Wählen Sie mindestens zwei Datensätze aus, die dieselbe Person beschreiben.') }}
        </p>
        <SphField
            :label="$t('Suche nach Name oder E-Mail')"
            label-for="record-link-member-search"
            class="my-4"
        >
            <SphInput
                id="record-link-member-search"
                v-model="query"
                :placeholder="$t('Mindestens zwei Zeichen eingeben ...')"
            />
        </SphField>

        <SphSpinner
            v-if="searching"
            variant="centered"
        />
        <table
            v-else-if="results.length > 0"
            class="kgr-table kgr-table--striped w-full"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--noGrow"></th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Name') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Geburtsdatum') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Verein') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('E-Mail') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="result in results"
                    :key="result.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <SphCheckbox
                            v-if="result.groupId === null"
                            :model-value="isSelected(result.id)"
                            @update:model-value="toggle(result.id)"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        {{ result.displayName }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ result.dateOfBirth }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ result.institutionName }}
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="result.groupId !== null">
                            {{ $t('Bereits verknüpft') }}
                        </template>
                        <template v-else>
                            {{ result.email }}
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
        <p v-else-if="query.trim().length >= 2 && !searching">
            {{ $t('Keine Datensätze gefunden.') }}
        </p>

        <SphField
            v-if="mode === 'create'"
            :label="$t('Notiz (optional)')"
            label-for="record-link-note"
            class="my-4"
        >
            <SphInput
                id="record-link-note"
                v-model="note"
            />
        </SphField>
    </SphDialog>
</template>
